import React, { useEffect, useRef, useState } from "react";

/**
 * ProtectedImage — best-effort deterrent against right-click save,
 * drag-to-disk and long-press-save on candidate photos in the
 * Open-for-Work category.
 *
 * Honest caveat: browser-side screenshot prevention is technically
 * impossible. A determined user can always:
 *   - Take a screen capture (OS-level, outside the browser's control)
 *   - Photograph the screen with another device
 *   - Open dev-tools and inspect the network response
 * What we CAN do is raise the friction for casual saves so it's not a
 * one-tap "Save Image" path:
 *   1. Disable native context menu (right-click) on the image
 *   2. Disable drag-and-drop (so the user can't drag to the desktop)
 *   3. Set `user-select: none` + iOS `-webkit-touch-callout: none` so
 *      long-press doesn't pop the "Save Image" sheet on mobile Safari
 *      and Chrome.
 *   4. Render the image as a CSS `background-image` on a div (no
 *      `<img>` element to right-click-save) AND overlay a 100 %
 *      transparent shield div that swallows pointer events EXCEPT
 *      clicks (we forward `onClick` so the parent <Link>/<Card> still
 *      works).
 *   5. A faint diagonal "ADUKOO · Open for Work" watermark overlay
 *      so any screenshot/photo that does leak is visibly marked.
 *
 * The `<img>` is also rendered with `aria-hidden` for accessibility
 * fallback when the background-image fails to load (rare CSS bug).
 */
export default function ProtectedImage({
    src,
    alt = "",
    className = "",
    objectFit = "contain",   // "contain" for portrait passport, "cover" otherwise
    objectPosition = "center", // v79.172 — symmetric cropping for `cover` avatars
    watermark = true,
    testId,
}) {
    const ref = useRef(null);
    const [blocked, setBlocked] = useState(false);

    // v77.28 — flag the element when devtools tries to inspect it.
    // We can't fully block devtools but we can blur the image when the
    // window loses focus (common while opening dev-tools, screenshot
    // apps, screen-share). Re-revealed on focus return.
    useEffect(() => {
        const onBlur = () => setBlocked(true);
        const onFocus = () => setBlocked(false);
        window.addEventListener("blur", onBlur);
        window.addEventListener("focus", onFocus);
        return () => {
            window.removeEventListener("blur", onBlur);
            window.removeEventListener("focus", onFocus);
        };
    }, []);

    const stop = (e) => { e.preventDefault(); e.stopPropagation(); return false; };

    return (
        <div
            ref={ref}
            data-testid={testId || "protected-image"}
            className={`relative overflow-hidden select-none aduku-protected-image ${className}`}
            style={{
                // v78.25 — Safari flicker fix. Previously the image was
                // rendered as a CSS background-image on this div, which
                // Safari repaints inefficiently on every grid re-render
                // (Chrome promotes background-image to its own layer;
                // Safari does not). We now render the <img> as the
                // visible element and keep the watermark + anti-save
                // shield on top. `transform: translateZ(0)` promotes the
                // wrapper into its own compositing layer so the blur
                // transition (still useful when the window loses focus)
                // doesn't trigger a full-page repaint.
                WebkitTouchCallout: "none",
                WebkitUserSelect: "none",
                MsUserSelect: "none",
                userSelect: "none",
                transform: "translateZ(0)",
            }}
            onContextMenu={stop}
            onDragStart={stop}
            onCopy={stop}
            aria-label={alt}
            role="img"
        >
            <img
                src={src}
                alt={alt}
                draggable={false}
                onContextMenu={stop}
                onDragStart={stop}
                style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit,
                    objectPosition,
                    // v78.56 — Chrome avatar-gap fix. When the wrapper is
                    // `rounded-full` + GPU-composited (translateZ), Chrome
                    // sometimes paints a hairline transparent ring between
                    // the rounded border and the inner image. Inheriting
                    // the wrapper's border-radius makes the image itself
                    // a clipped circle (no reliance on parent overflow
                    // clipping the rectangular bitmap). Backface-hidden
                    // forces an integer-pixel paint so we don't get
                    // subpixel cracks on HiDPI / 1.25-1.5× zoom screens.
                    borderRadius: "inherit",
                    backfaceVisibility: "hidden",
                    WebkitBackfaceVisibility: "hidden",
                    // Blur on blocked state kept for the anti-screenshot
                    // deterrent. No CSS `transition` here — that's what
                    // caused the per-frame Safari repaint. State change
                    // is rare (window blur/focus) so a hard swap is fine.
                    filter: blocked ? "blur(20px) saturate(0.5)" : "none",
                }}
            />
            {watermark && (
                <div
                    aria-hidden="true"
                    className="absolute inset-0 pointer-events-none flex items-center justify-center"
                    style={{
                        background: "linear-gradient(135deg, transparent 45%, rgba(15, 23, 42, 0.06) 45%, rgba(15, 23, 42, 0.06) 55%, transparent 55%)",
                    }}
                >
                    <div
                        className="font-display font-black text-aduku-navy/15 text-[10px] tracking-[0.4em] uppercase rotate-[-22deg] whitespace-nowrap"
                        style={{ textShadow: "0 1px 0 rgba(255,255,255,0.45)" }}
                    >
                        ADUKOO · OPEN FOR WORK
                    </div>
                </div>
            )}
            {/* Transparent pointer-shield — swallows context/drag events
                without intercepting clicks (parent <Link> needs them). */}
            <div
                aria-hidden="true"
                className="absolute inset-0"
                onContextMenu={stop}
                onDragStart={stop}
                style={{ pointerEvents: "none" }}
            />
        </div>
    );
}
